import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {OpenbandService} from "../../services/openband/openband.service";
import {NotificationService} from "../../services/notifications/notification.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {Title} from "@angular/platform-browser";
import {PreferencesService} from "../../services/preferences/preferences.service";
import {Log, PageContent} from "../../domain/models";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss']
})
export class LogsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  expandedJsons = new Set<string>();
  displayedColumns: string[] = ['id', 'timestamp', 'action', 'data', 'actions'];
  dataSource: MatTableDataSource<Log> = new MatTableDataSource<Log>([]);
  pageInfo: PageContent = new PageContent();
  currentPage: number = 0;
  isLoading: boolean = false;
  currentAction: string = "all";
  users: string[] = [""];
  currentUser: string;
  beginTimestamp: string = "";
  endTimestamp: string = "";
  searchDataQuery: string = "";
  visiblePages: number[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: OpenbandService,
    private notifyService: NotificationService,
    private modalService: NgbModal,
    private titleService: Title,
    private prefs: PreferencesService,
  ) {
    titleService.setTitle(`Логи`);
    this.loadUsers()
  }

  loadUsers() {
    this.api.getUsers().subscribe(
      x => {
        x.map(x => x.username).forEach(x =>
          this.users.push(x)
        )
      })
  }

  ngOnInit() {
    this.loadLogs();
  }

  ngAfterViewInit() {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.pageInfo.page.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadLogs();
    }
  }

  goToFirstPage() {
    this.goToPage(0);
  }

  goToLastPage() {
    this.goToPage(this.pageInfo.page.totalPages - 1);
  }

  updateVisiblePages() {
    const maxVisible = 5;
    const pages: number[] = [];

    if (this.pageInfo.page.totalPages <= maxVisible) {
      for (let i = 0; i < this.pageInfo.page.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(0, this.currentPage - 2);
      const end = Math.min(this.pageInfo.page.totalPages - 1, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    this.visiblePages = pages;
  }

  convertISOtoUTC(isoString) {
    try {
      const date = new Date(isoString);
      return date.toISOString();
    } catch (e) {
      return null
    }
  }

  loadLogs() {
    let beginDate = this.convertISOtoUTC(this.beginTimestamp + ":00+00:00")
    if (beginDate == null) beginDate = ""
    let endDate = this.convertISOtoUTC(this.endTimestamp + ":00+00:00")
    if (endDate == null) endDate = ""
    this.isLoading = true;
    if (this.currentUser == null) this.currentUser = ""
    this.api.getLogsNew(
      null,
      this.currentPage,
      this.pageInfo?.page?.size,
      this.currentAction,
      this.currentUser,
      beginDate,
      endDate,
      this.searchDataQuery,
    ).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      res => {
        this.pageInfo = res
        this.updateVisiblePages();
      },
      error => {
        this.notifyService.showError(error.error?.message || 'Ошибка при загрузке логов');
      }
    );
  }

  getPrettyJson(json: string): string {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch (e) {
      return JSON.stringify(json, null, 2);
    }
  }

  canRevertChanges(log) {
    if (log.action == "participants.update") {
      return true;
    }
    return false;
  }

  revertChanges(log) {
    if (log.action == "participants.update") {
      if (confirm("Вы действительно хотите восстановить предыдущие данные по участнику " + log.data.before.id + " ?")) {
        this.api.updateOnlyParticipant(log.data.before).subscribe(
          result => {
            this.notifyService.showSuccess("Данные восстановлены");
          },
          error => {
            this.notifyService.showError("Ошибка при восстановлении данных");
          }
        );
      }
    }
  }

  shouldShowFirstPage(): boolean {
    return this.visiblePages.length > 0 && this.visiblePages[0] > 0;
  }

  shouldShowLastPage(): boolean {
    return this.visiblePages.length > 0 && this.visiblePages[this.visiblePages.length - 1] < this.pageInfo.page.totalPages - 1;
  }

  shouldShowLeftEllipsis(): boolean {
    return this.visiblePages.length > 0 && this.visiblePages[0] > 1;
  }

  shouldShowRightEllipsis(): boolean {
    return this.visiblePages.length > 0 && this.visiblePages[this.visiblePages.length - 1] < this.pageInfo.page.totalPages - 2;
  }

  canGoPrev(): boolean {
    return this.currentPage > 0;
  }

  canGoNext(): boolean {
    return this.currentPage < this.pageInfo.page.totalPages - 1;
  }

  getActionIconClass(action: string): string {
    if (action.includes('create')) return 'create';
    if (action.includes('update')) return 'update';
    if (action.includes('delete')) return 'delete';
    return 'default';
  }

  getActionText(action: string): string {
    if (action.includes('create')) return 'Создание';
    if (action.includes('update')) return 'Обновление';
    if (action.includes('delete')) return 'Удаление';
    return action;
  }

  prevPage() {
    this.currentPage--
    this.loadLogs()
  }

  nextPage() {
    this.currentPage++
    this.loadLogs()
  }

  getLogCardClass(log: any): string {
    const baseClass = 'log-card';
    if (log.action.includes('update')) return `${baseClass} update-action`;
    if (log.action.includes('create')) return `${baseClass} create-action`;
    if (log.action.includes('upload')) return `${baseClass} upload-action`;
    if (log.action.includes('clear')) return `${baseClass} delete-action`;
    return baseClass;
  }

  getActionClass(action: string): string {
    if (action.includes('update')) return 'action-update';
    if (action.includes('create')) return 'action-create';
    if (action.includes('upload')) return 'action-upload';
    if (action.includes('clear')) return 'action-delete';
    return 'action-default';
  }

  getActionIcon(action: string): string {
    if (action.includes('update')) return 'bi bi-pencil-square';
    if (action.includes('create')) return 'bi bi-plus-circle';
    if (action.includes('upload')) return 'bi bi-upload';
    if (action.includes('clear')) return 'bi bi-trash';
    if (action.includes('download')) return 'bi bi-download';
    return 'bi bi-gear';
  }

  getActionDisplayName(action: string): string {
    const actionMap: { [key: string]: string } = {
      'participants.update': 'Изменение участника',
      'participants.upload': 'Загрузка участников',
      'participants.download': 'Скачивание участников',
      'events.create': 'Создание события',
      'tag_bibs.upload': 'Загрузка номеров',
      'events.tag-bib.clear': 'Очистка номеров'
    };
    return actionMap[action] || action;
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('ru-RU');
  }

  hasBeforeAfter(log: any): boolean {
    return log.data && log.data.before && log.data.after;
  }

  getParticipantName(participant: any): string {
    return `${participant.name} ${participant.surname}`;
  }

  getChanges(data: any): any[] {
    if (!data.before || !data.after) return [];

    const changes = [];
    const before = data.before;
    const after = data.after;

    for (const key in after) {
      if (before[key] !== after[key]) {
        changes.push({
          field: key,
          before: before[key],
          after: after[key]
        });
      }
    }

    return changes;
  }

  getFieldDisplayName(field: string): string {
    const fieldMap: { [key: string]: string } = {
      'bib': 'Номер',
      'tag': 'Чип',
      'name': 'Имя',
      'surname': 'Фамилия',
      'phone': 'Телефон',
      'club': 'Клуб',
      'event': 'Дистанция',
      'estimatedStart': 'Время старта'
    };
    return fieldMap[field] || field;
  }

  isUploadAction(action: string): boolean {
    return action.includes('upload');
  }

  getUploadDisplayName(action: string): string {
    if (action === 'participants.upload') return 'Загрузка участников';
    if (action === 'tag_bibs.upload') return 'Загрузка номеров';
    return 'Загрузка файла';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return Math.round(bytes / 1048576) + ' MB';
  }

  isSpecialCase(log: any): boolean {
    return this.hasBeforeAfter(log) ||
      log.action === 'events.create' ||
      this.isUploadAction(log.action);
  }

  toggleJsonExpanded(logId: string): void {
    if (this.expandedJsons.has(logId)) {
      this.expandedJsons.delete(logId);
    } else {
      this.expandedJsons.add(logId);
    }
  }

  isJsonExpanded(logId: string): boolean {
    return this.expandedJsons.has(logId);
  }

  getJsonSize(data: any): string {
    const str = JSON.stringify(data);
    return `${str.length} символов`;
  }

  copyLogData(log: any): void {
    navigator.clipboard.writeText(JSON.stringify(log.data, null, 2));
    // Показать уведомление об успешном копировании
  }

  showLogDetails(log: any): void {
    // Открыть модальное окно с подробной информацией
  }
}
